import React from "react";
import {inject, observer} from "mobx-react";
import {Alert, Button, Card, CardBody, CardFooter, Container} from "reactstrap";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import {Link} from "react-router-dom";
import getRoutes from "../../../../components/Akitatek/getRoutes";
import PriseEnChargeAssistantAPI from "../../../../util/priseenchargeAssistant";
import {donneesPourEnregistrer, nomAppareil} from "../../../../util/assistantPec";
import EtapeClient from "./EtapeClient";
import EtapeAppareil from "./EtapeAppareil";
import EtapeAccessoires from "./EtapeAccessoires";
import EtapeSerie from "./EtapeSerie";
import EtapeRecap from "./EtapeRecap";
import "./AssistantPriseEnCharge.css";

const ETAPES = ["Client", "Appareil et réparation", "Accessoires et panne", "Numéro de série", "Impression"];
const ICONES = ["fa-user", "fa-wrench", "fa-plug", "fa-barcode", "fa-print"];

const nouvelleLigne = () => ({
    appareil: null,
    userAppareilId: null,
    prestations: [],
    accessoires: [],
    accessoiresAutre: "",
    defauts: "",
    code: "",
    serial: "",
});

function IconeEtape({active, completed, icon}) {
    return (
        <div className={"assistant-pec-icone" + (active ? " active" : "") + (completed ? " faite" : "")}>
            <i className={"fa " + (completed ? "fa-check" : ICONES[icon - 1])}/>
        </div>
    );
}

// Les actions d'impression du store attendent un évènement de clic.
const sansEvenement = {preventDefault: () => {}};

/**
 * Création d'une prise en charge au comptoir, étape par étape : client, appareil et réparation, accessoires,
 * numéro de série, puis impression. Rien n'est enregistré avant la dernière étape.
 * Modifier une prise en charge existante se fait toujours dans « Gérer une Prise En Charge ».
 */
const AssistantPriseEnCharge = inject("rootStore")(observer(class AssistantPriseEnCharge extends React.Component {

    api = new PriseEnChargeAssistantAPI();

    get injected() {
        return this.props;
    }

    constructor(props, context) {
        super(props, context);
        this.injected.rootStore.savedDatasStore.setHeader("Prises En Charge", "Nouvelle prise en charge", "priseencharge.png");
        this.state = this.etatInitial();
    }

    etatInitial() {
        return {
            etape: 0,
            client: null,
            lignes: [],
            ligne: nouvelleLigne(),
            indexEdition: null,
            titre: "",
            descriptionTech: "",
            description: "",
            imprimerEtiquettes: true,
            imprimerPapier: false,
            enregistrement: false,
            pec: null,
            erreur: "",
        };
    }

    async componentDidMount() {
        const client = this.injected.location?.aboutProps?.user;
        if (client?.id) {
            await this.choisirClient(client);
        }
    }

    choisirClient = async (client) => {
        if (!client) {
            this.setState({client: null});
            return;
        }
        this.setState({client});
        try {
            const complet = await this.api.client(client.id);
            this.setState({client: complet, etape: this.state.etape === 0 ? 1 : this.state.etape});
        } catch (e) {
            this.setState({etape: 1});
        }
    };

    changerLigne = (modifs) => this.setState(state => ({ligne: {...state.ligne, ...modifs}}));

    erreur = (message) => this.setState({erreur: message});

    peutAvancer() {
        const {etape, client, ligne, lignes} = this.state;
        if (etape === 0) return !!client?.id;
        if (etape === 1) return !!ligne.appareil;
        if (etape === 4) return lignes.length > 0;
        return true;
    }

    suivant = () => {
        const {etape, ligne, lignes, indexEdition} = this.state;
        if (etape === 3) {
            const nouvelles = [...lignes];
            if (indexEdition !== null) {
                nouvelles[indexEdition] = ligne;
            } else {
                nouvelles.push(ligne);
            }
            this.setState({lignes: nouvelles, ligne: nouvelleLigne(), indexEdition: null, etape: 4});
            return;
        }
        if (etape === 4) {
            this.enregistrer();
            return;
        }
        this.setState({etape: etape + 1, erreur: ""});
    };

    precedent = () => {
        const {etape, lignes} = this.state;
        if (etape === 1 && lignes.length > 0) {
            // On ajoutait ou modifiait un appareil : retour au récapitulatif sans rien changer.
            this.setState({etape: 4, ligne: nouvelleLigne(), indexEdition: null});
            return;
        }
        this.setState({etape: Math.max(0, etape - 1), erreur: ""});
    };

    allerA(index) {
        if (this.state.pec || index >= this.state.etape) {
            return;
        }
        if (this.state.etape === 4 && index > 0) {
            return; // depuis le récapitulatif, on modifie un appareil avec son bouton
        }
        this.setState({etape: index, erreur: ""});
    }

    async enregistrer() {
        const {client, lignes, titre, descriptionTech, description, imprimerEtiquettes, imprimerPapier} = this.state;
        this.setState({enregistrement: true, erreur: ""});
        try {
            const pec = await this.api.enregistrer(donneesPourEnregistrer(client, lignes, descriptionTech, description, titre));
            this.setState({pec});
            this.injected.rootStore.notificationStore.setNotification("Prise en charge n° " + pec.id + " enregistrée", "success");
            if (this.impressionPossible()) {
                if (imprimerEtiquettes) {
                    await this.injected.rootStore.prisenchargeStore.downloadPdf(sansEvenement, pec.id, "all", "");
                }
                if (imprimerPapier) {
                    await this.injected.rootStore.prisenchargeStore.openPdfPec(sansEvenement, pec.id, "1");
                }
            }
        } catch (e) {
            const erreurs = e?.response?.data?.errors;
            this.setState({
                erreur: erreurs ? Object.values(erreurs).flat().join(" ")
                    : (e?.response?.data?.message || "L'enregistrement a échoué. Rien n'a été créé, vous pouvez réessayer."),
            });
        }
        this.setState({enregistrement: false});
    }

    impressionPossible() {
        return this.injected.rootStore.authStore.modules?.['print'] === 1;
    }

    contenu() {
        const {etape, client, ligne, lignes} = this.state;
        switch (etape) {
            case 0:
                return <EtapeClient api={this.api} client={client} onChoisir={this.choisirClient}/>;
            case 1:
                return <EtapeAppareil api={this.api} client={client} ligne={ligne} onChange={this.changerLigne}
                                      onErreur={this.erreur}/>;
            case 2:
                return <EtapeAccessoires ligne={ligne} onChange={this.changerLigne}/>;
            case 3:
                return <EtapeSerie ligne={ligne} onChange={this.changerLigne}/>;
            default:
                return <EtapeRecap
                    client={client}
                    lignes={lignes}
                    titre={this.state.titre}
                    descriptionTech={this.state.descriptionTech}
                    description={this.state.description}
                    imprimerEtiquettes={this.state.imprimerEtiquettes}
                    imprimerPapier={this.state.imprimerPapier}
                    impressionPossible={this.impressionPossible()}
                    onChange={(modifs) => this.setState(modifs)}
                    onModifier={(index) => this.setState({ligne: lignes[index], indexEdition: index, etape: 1})}
                    onRetirer={(index) => this.setState({lignes: lignes.filter((l, i) => i !== index)})}
                    onAjouter={() => this.setState({ligne: nouvelleLigne(), indexEdition: null, etape: 1})}
                />;
        }
    }

    termine() {
        const {pec, lignes} = this.state;
        const store = this.injected.rootStore.prisenchargeStore;
        const impression = this.impressionPossible();

        return (
            <div className="text-center py-4">
                <div className="assistant-pec-ok"><i className="fa fa-check"/></div>
                <h2>Prise en charge n° {pec.id} enregistrée</h2>
                <p className="text-muted">{pec.user?.fullname} · {lignes.map(l => nomAppareil(l.appareil)).join(", ")}</p>
                <div className="assistant-pec-actions">
                    {impression && (
                        <Button color="primary" onClick={e => store.downloadPdf(e, pec.id, "all", "")}>
                            <i className="fa fa-tag mr-2"/>Imprimer les étiquettes
                        </Button>
                    )}
                    {impression && (
                        <Button color="primary" onClick={e => store.openPdfPec(e, pec.id, "1")}>
                            <i className="fa fa-print mr-2"/>Imprimer le papier client
                        </Button>
                    )}
                    <Button outline color="secondary" onClick={e => store.openPdf(e, "all", pec.id)}>Voir les étiquettes</Button>
                    <Button outline color="secondary" onClick={e => store.openPdfPec(e, pec.id, "0")}>Voir le papier client</Button>
                </div>
                <div className="assistant-pec-actions mt-3">
                    <Link className="btn btn-outline-primary" to={getRoutes("Gérer une Prise En Charge", pec.id, "0")}>
                        Ouvrir la fiche
                    </Link>
                    <Button color="success" onClick={() => this.setState(this.etatInitial())}>
                        <i className="fa fa-plus mr-2"/>Nouvelle prise en charge
                    </Button>
                </div>
            </div>
        );
    }

    render() {
        const {etape, pec, enregistrement, erreur, indexEdition, lignes} = this.state;
        const libelleSuivant = etape === 3 ? (indexEdition !== null ? "Valider les modifications" : "Valider cet appareil")
            : etape === 4 ? (enregistrement ? "Enregistrement…" : "Enregistrer la prise en charge") : "Suivant";
        const retourRecap = etape === 1 && lignes.length > 0;

        return (
            <Container className="mt-2 assistant-pec" fluid>
                <Card>
                    <Stepper activeStep={pec ? ETAPES.length : etape} alternativeLabel className="assistant-pec-stepper">
                        {ETAPES.map((label, index) => (
                            <Step key={label} onClick={() => this.allerA(index)}
                                  style={{cursor: !pec && index < etape && (etape < 4 || index === 0) ? "pointer" : "default"}}>
                                <StepLabel StepIconComponent={IconeEtape}>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    <CardBody>
                        {erreur && <Alert color="danger" toggle={() => this.setState({erreur: ""})}>{erreur}</Alert>}
                        {pec ? this.termine() : this.contenu()}
                    </CardBody>
                    {!pec && (
                        <CardFooter className="d-flex justify-content-between">
                            <Button outline color="primary" disabled={etape === 0 || etape === 4 || enregistrement} onClick={this.precedent}>
                                {retourRecap ? "Retour au récapitulatif" : "Précédent"}
                            </Button>
                            {etape > 0 || this.state.client ? (
                                <Button color={etape === 4 ? "success" : "primary"} size={etape === 4 ? "lg" : undefined}
                                        disabled={!this.peutAvancer() || enregistrement} onClick={this.suivant}>
                                    {libelleSuivant}
                                </Button>
                            ) : <span/>}
                        </CardFooter>
                    )}
                </Card>
            </Container>
        );
    }
}));

export default AssistantPriseEnCharge;
